import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'react-redux'
import {
  GET_CONTRACTS_CONFIGURATION_BY_SLUG_QUERY_KEY,
  useGetConfigurationBySlug,
  useUpsertConfiguration,
} from 'src/api/queries/contracts'
import { ContractsContextProvider, useContractsContext } from 'src/reports/contracts/context'
import DetailsCard from 'src/reports/contracts/details-card'
import ConfirmationModal from 'src/reports/contracts/confirmation-modal'
import { EmptyState, FailedState } from 'src/ui/placeholders'
import {
  Box,
  Button,
  Card,
  Center,
  HStack,
  InfoOutlineIcon,
  Skeleton,
  Spinner,
  Stack,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from 'src/ui'

function Contracts() {
  const queryClient = useQueryClient()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const companySlug = useStore().getState().settings.companySlug
  const toast = useToast()
  const { setDetailsIsEditing } = useContractsContext()

  const {
    data: configurationResponse,
    isLoading: getConfigBySlugIsLoading,
    isFetching: getConfigBySlugIsFetching,
    error: getConfigBySlugError,
  } = useGetConfigurationBySlug({
    slug: companySlug,
  })
  const { mutateAsync: upsertConfigurationAsync, isPending: upsertConfigIsLoading } =
    useUpsertConfiguration({ slug: companySlug })

  if (getConfigBySlugIsLoading || getConfigBySlugIsFetching) {
    return (
      <Stack spacing={8}>
        <Skeleton h="56px" />
        <Skeleton h="154px" />
        <Skeleton h="238px" />
      </Stack>
    )
  }

  if (getConfigBySlugError) {
    if (getConfigBySlugError.response.status === 404)
      return (
        <Center flexDirection="column">
          <EmptyState message="Your company hasn’t setup Contracts reporting. Click below to start!">
            Error
          </EmptyState>
          <Button
            size="sm"
            colorScheme="primary"
            maxW="143px"
            onClick={() => {
              upsertConfigurationAsync({
                title: 'Contracts',
                enabled: false,
              })
                .then(() => {
                  queryClient.refetchQueries({
                    queryKey: [GET_CONTRACTS_CONFIGURATION_BY_SLUG_QUERY_KEY],
                  })
                  toast({
                    status: 'success',
                    title: 'Success',
                    isClosable: true,
                    description: 'Successfully added company slug',
                    position: 'top-right',
                  })
                })
                .catch(() => {
                  toast({
                    status: 'error',
                    title: 'Error',
                    isClosable: true,
                    description: 'Failed to add company slug',
                    position: 'top-right',
                  })
                })
            }}
            isDisabled={upsertConfigIsLoading}
            isLoading={upsertConfigIsLoading}
          >
            Add Company
          </Button>
        </Center>
      )
    else {
      return <FailedState />
    }
  }

  const {
    data: { enabled, title },
  } = configurationResponse

  return (
    <>
      {isOpen && (
        <ConfirmationModal
          isOpen={isOpen}
          onClose={onClose}
          onConfirm={() => {
            upsertConfigurationAsync({
              title,
              enabled: !enabled,
            })
              .then(() => {
                queryClient.refetchQueries({
                  queryKey: [GET_CONTRACTS_CONFIGURATION_BY_SLUG_QUERY_KEY],
                })
                toast({
                  status: 'success',
                  title: 'Success',
                  isClosable: true,
                  description: 'Successfully turned off Contracts Reporting',
                  position: 'top-right',
                })
                onClose()
                setDetailsIsEditing(false)
              })
              .catch(() => {
                toast({
                  status: 'error',
                  title: 'Error',
                  isClosable: true,
                  description: 'Failed to turn off Contracts Reporting',
                  position: 'top-right',
                })
                onClose()
              })
          }}
          isLoading={upsertConfigIsLoading}
        />
      )}
      <Stack spacing={8}>
        <Card>
          <HStack justify="space-between">
            <Box>
              <Text fontWeight="bold">Contracts Report</Text>
              <Text fontSize="sm" color="gray.400">
                <InfoOutlineIcon mb={0.5} mr={1} />
                This feature is only compatible with companies using contracts version 3.0.0
              </Text>
            </Box>

            {upsertConfigIsLoading && !enabled ? (
              <Spinner />
            ) : (
              <Switch
                isChecked={enabled}
                onChange={() => {
                  if (enabled) {
                    onOpen()
                  } else {
                    upsertConfigurationAsync({
                      title,
                      enabled: !enabled,
                    })
                      .then(() => {
                        queryClient.refetchQueries({
                          queryKey: [GET_CONTRACTS_CONFIGURATION_BY_SLUG_QUERY_KEY],
                        })
                        toast({
                          status: 'success',
                          title: 'Success',
                          isClosable: true,
                          description: 'Successfully turned on Contracts Reporting',
                          position: 'top-right',
                        })
                      })
                      .catch(() => {
                        toast({
                          status: 'error',
                          title: 'Error',
                          isClosable: true,
                          description: 'Failed to turn off Contracts Reporting',
                          position: 'top-right',
                        })
                      })
                  }
                }}
              />
            )}
          </HStack>
        </Card>
        {enabled && <DetailsCard title={title} />}
      </Stack>
    </>
  )
}

function ContractsWithContext() {
  return (
    <ContractsContextProvider>
      <Contracts />
    </ContractsContextProvider>
  )
}

export default ContractsWithContext
